import { useTranslation } from "react-i18next";
import { Bot, Check, Star, Loader2, Plus } from "lucide-react";
import { Button, Badge } from "@tabipla/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@tabipla/ui/components/dialog";
import { usePackingAiSuggestions } from "./PackingContext";

export function AiSuggestionsDialog() {
  const { t } = useTranslation();
  const {
    aiSuggestions: suggestions,
    selectedSuggestions,
    showAiPanel: open,
    isBulkCreating,
    onToggleSuggestion,
    onSelectAllSuggestions: onSelectAll,
    onDeselectAllSuggestions: onDeselectAll,
    onAddSelectedSuggestions: onAddSelected,
    onCloseAiPanel,
  } = usePackingAiSuggestions();

  return (
    <Dialog
      open={open && suggestions.length > 0}
      onOpenChange={(isOpen) => !isOpen && onCloseAiPanel()}
    >
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="flex max-h-[70vh] flex-col gap-0 overflow-hidden p-0 sm:flex sm:max-h-[80vh] sm:max-w-lg sm:flex-col sm:p-0"
      >
        <DialogHeader className="shrink-0 border-b px-4 pb-3 pt-4 sm:px-6 sm:pt-6">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            <DialogTitle>{t("packing.aiSuggestions")}</DialogTitle>
            <Badge variant="secondary">{suggestions.length}</Badge>
          </div>
          <DialogDescription className="sr-only">
            {t("packing.aiSuggestDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center justify-between border-b px-4 py-2">
            <span className="text-sm text-muted-foreground">
              {selectedSuggestions.size} {t("packing.selected")}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onSelectAll}>
                {t("packing.selectAll")}
              </Button>
              <Button variant="ghost" size="sm" onClick={onDeselectAll}>
                {t("packing.deselectAll")}
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 divide-y overflow-y-auto">
            {suggestions.map((suggestion, index) => {
              const isSelected = selectedSuggestions.has(index);
              return (
                <div
                  key={index}
                  onClick={() => onToggleSuggestion(index)}
                  className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/50 ${
                    isSelected ? "bg-primary/5" : ""
                  }`}
                >
                  <div
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                      isSelected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-muted-foreground/30"
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {suggestion.isEssential && (
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      )}
                      <span className="font-medium">{suggestion.name}</span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{suggestion.reason}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="shrink-0 border-t p-4">
          <Button
            onClick={onAddSelected}
            disabled={selectedSuggestions.size === 0 || isBulkCreating}
            className="w-full"
          >
            {isBulkCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("packing.adding")}
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                {t("packing.addSelected", { count: selectedSuggestions.size })}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
